import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CreditCard } from "lucide-react";
import axios from "axios";
import { API, PACKAGES, pageVariants } from "../components/app/constants";
import { SEO } from "../components/shared/SEO";

export const PricingPage = ({ user, onNavigate }) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [firstPurchaseDiscount, setFirstPurchaseDiscount] = useState(false);
  const [weeklyCredited, setWeeklyCredited] = useState(false);
  const [weeklyMsg, setWeeklyMsg] = useState("");
  const [claimingWeekly, setClaimingWeekly] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/promotions/${user.id}`).then(res => {
        setFirstPurchaseDiscount(res.data.first_purchase_discount);
      }).catch(() => {});
      axios.get(`${API}/subscriptions/status/${user.id}`).then(res => {
        setSubStatus(res.data);
      }).catch(() => {});
    }
  }, [user]);

  const handlePurchase = async (packageId) => {
    if (!user) {
      onNavigate(`/start?buy=${packageId}`);
      return;
    }
    
    setLoading(packageId);
    
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        user_id: user.id,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      alert("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  // Auto-resume checkout after login: if user arrives at /pricing?buy=<pkg> while authenticated,
  // clean the URL and immediately trigger Stripe checkout for that package.
  useEffect(() => {
    const buyParam = searchParams.get("buy");
    if (user && buyParam && PACKAGES.some(p => p.id === buyParam)) {
      window.history.replaceState({}, "", window.location.pathname);
      handlePurchase(buyParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const claimWeeklyCredit = async () => {
    if (!user) return;
    setClaimingWeekly(true);
    try {
      const res = await axios.post(`${API}/credits/weekly-free`);
      setWeeklyMsg(res.data.message);
      setWeeklyCredited(res.data.success);
    } catch (err) {
      setWeeklyMsg("Please sign in to claim");
    }
    setClaimingWeekly(false);
  };

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pt-32 pb-20 px-6"
    >
      <SEO title="Pricing — AI Portrait Credits" path="/pricing" description="StarFrame AI pricing: 3 free credits on signup. Starter Pack $10 (7 credits), Pro Pack $29.99 (25 credits), Ultimate Pack $79.99 (150 credits). 50% off your first purchase. No subscription required." />
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Pricing</p>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight mb-4">
            Invest in Your Image
          </h1>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            1 credit = 1 portrait. Start free, then buy more when you're ready.
          </p>
        </div>

        {/* First Purchase Discount Banner */}
        {user && firstPurchaseDiscount && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-gradient-to-r from-[#D4AF37]/20 via-[#D4AF37]/10 to-[#D4AF37]/20 border border-[#D4AF37]/40 rounded-xl text-center"
            data-testid="first-purchase-banner"
          >
            <p className="text-[#D4AF37] font-bold text-lg">First Purchase — 50% OFF!</p>
            <p className="text-white/60 text-sm">Your first credit pack is half price. Don't miss out!</p>
          </motion.div>
        )}

        {/* Weekly Free Credit */}
        {user && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8 p-4 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between"
            data-testid="weekly-credit-section"
          >
            <div>
              <p className="text-sm font-medium">Weekly Free Credit</p>
              <p className="text-xs text-white/50">Come back every week for 1 free credit</p>
            </div>
            {weeklyMsg ? (
              <p className={`text-sm ${weeklyCredited ? 'text-green-400' : 'text-white/50'}`}>{weeklyMsg}</p>
            ) : (
              <button 
                onClick={claimWeeklyCredit}
                disabled={claimingWeekly}
                className="px-4 py-2 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] rounded-lg text-sm font-medium transition-colors"
                data-testid="claim-weekly-btn"
              >
                {claimingWeekly ? "Claiming..." : "Claim Free Credit"}
              </button>
            )}
          </motion.div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PACKAGES.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`pricing-card p-8 relative ${pkg.featured ? 'featured ring-2 ring-[#D4AF37]/50' : ''}`}
              data-testid={`pricing-card-${pkg.id}`}
            >
              {pkg.featured && (
                <>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-wider rounded-full">Best Value</div>
                  <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 mt-2 font-semibold">Most Popular</div>
                </>
              )}
              <h3 className="font-serif text-2xl mb-2">{pkg.name}</h3>
              <div className="flex items-baseline gap-2 mb-1">
                {user && firstPurchaseDiscount && (
                  <span className="text-white/30 line-through text-lg">${pkg.price}</span>
                )}
                <span className="font-serif text-5xl gold-text">
                  ${user && firstPurchaseDiscount ? (pkg.price / 2).toFixed(2) : pkg.price}
                </span>
              </div>
              {user && firstPurchaseDiscount && (
                <p className="text-green-400 text-xs mb-3 font-medium">50% OFF first purchase!</p>
              )}
              {pkg.id === "ultimate" && (
                <p className="text-green-400 text-xs mb-2 font-medium">Lowest price per portrait!</p>
              )}
              
              <div className="space-y-3 mb-8 mt-4">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <span>{pkg.credits} Portraits</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <span className={pkg.id === "ultimate" ? "text-[#D4AF37] font-medium" : ""}>
                    ${(pkg.price / pkg.credits).toFixed(2)}/portrait
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <span>All 12 Styles</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <span>HD Downloads</span>
                </div>
                {pkg.id === "ultimate" && (
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-green-400 font-medium">Best value on the market</span>
                  </div>
                )}
              </div>
              
              <button 
                onClick={() => handlePurchase(pkg.id)}
                disabled={loading === pkg.id}
                className={`w-full ${pkg.featured ? 'btn-primary' : 'btn-secondary'} flex items-center justify-center gap-2`}
                data-testid={`buy-${pkg.id}`}
              >
                {loading === pkg.id ? (
                  <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    {pkg.featured ? 'Get Best Deal' : 'Buy Now'}
                  </>
                )}
              </button>
            </motion.div>
          ))}
        </div>
        
        
        {user && (
          <p className="text-center text-white/40 mt-8" data-testid="current-balance">
            Current balance: <span className="text-[#D4AF37]">{user.credits} credits</span>
          </p>
        )}
      </div>
    </motion.div>
  );
};


